import { useState } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  TextInput,
  Share,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useRouter, useLocalSearchParams } from "expo-router";
import * as DocumentPicker from "expo-document-picker";
import { createRun } from "../../lib/api";
import { formatTime } from "../../lib/format";
import { Ionicons } from "@expo/vector-icons";
import { Button } from "../../../components/Button";

interface ParsedRun {
  date: string;
  timeSeconds: number;
  runNumber: number;
  notes?: string;
}

function parseCSV(text: string): { runs: ParsedRun[]; errors: string[] } {
  const lines = text.trim().split("\n");
  const runs: ParsedRun[] = [];
  const errors: string[] = [];

  // Skip header if present
  const firstLine = lines[0].toLowerCase();
  const startIdx = firstLine.includes("date") || firstLine.includes("time") ? 1 : 0;

  for (let i = startIdx; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const parts = line.split(",").map((s) => s.trim().replace(/^["']|["']$/g, ""));

    if (parts.length < 2) {
      errors.push(`Row ${i + 1}: need at least date and time`);
      continue;
    }

    const date = parts[0];
    const timeSeconds = parseFloat(parts[1]);
    const runNumber = parts[2] ? parseInt(parts[2], 10) : 1;
    const notes = parts[3] || undefined;

    // Validate date format
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      errors.push(`Row ${i + 1}: date must be YYYY-MM-DD (got "${date}")`);
      continue;
    }

    if (isNaN(timeSeconds) || timeSeconds <= 0 || timeSeconds > 60) {
      errors.push(`Row ${i + 1}: time must be between 0 and 60 seconds (got "${parts[1]}")`);
      continue;
    }

    if (runNumber !== 1 && runNumber !== 2) {
      errors.push(`Row ${i + 1}: run number must be 1 or 2 (got "${parts[2]}")`);
      continue;
    }

    runs.push({ date, timeSeconds, runNumber, notes });
  }

  return { runs, errors };
}

export default function ImportRunsScreen() {
  const router = useRouter();
  const { dogId } = useLocalSearchParams<{ dogId: string }>();
  const [csvText, setCsvText] = useState("");
  const [parsed, setParsed] = useState<{ runs: ParsedRun[]; errors: string[] } | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: number; failed: number } | null>(null);
  const [error, setError] = useState("");

  async function handleFilePick() {
    setError("");
    if (Platform.OS === "web") {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".csv,.txt";
      input.onchange = (e: any) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
          const text = ev.target?.result as string;
          setCsvText(text);
          setParsed(parseCSV(text));
          setImportResult(null);
        };
        reader.readAsText(file);
      };
      input.click();
      return;
    }
    // Native: use document picker → fetch the file:// URI as text.
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["text/csv", "text/comma-separated-values", "text/plain", "public.comma-separated-values-text"],
        copyToCacheDirectory: true,
        multiple: false,
      });
      if (result.canceled || !result.assets || result.assets.length === 0) return;
      const asset = result.assets[0];
      const res = await fetch(asset.uri);
      const text = await res.text();
      setCsvText(text);
      setParsed(parseCSV(text));
      setImportResult(null);
    } catch (e: any) {
      setError(e?.message || "Couldn't read that file");
    }
  }

  function handlePaste(text: string) {
    setCsvText(text);
    setParsed(parseCSV(text));
    setImportResult(null);
  }

  async function handleImport() {
    if (!parsed || parsed.runs.length === 0) return;
    setImporting(true);
    setError("");

    let success = 0;
    let failed = 0;

    for (const run of parsed.runs) {
      try {
        await createRun(dogId!, {
          date: run.date,
          timeSeconds: run.timeSeconds,
          runNumber: run.runNumber,
          notes: run.notes,
        } as any);
        success++;
      } catch (err: any) {
        failed++;
      }
    }

    setImporting(false);
    setImportResult({ success, failed });
  }

  const templateCSV = "date,time_seconds,run_number,notes\n2026-01-15,8.23,1,First event\n2026-01-15,7.95,2,Second run\n2026-02-20,8.01,1,\n2026-02-20,7.79,2,Personal best!";

  async function downloadTemplate() {
    if (Platform.OS === "web") {
      const blob = new Blob([templateCSV], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "dashhound-runs-template.csv";
      a.click();
      URL.revokeObjectURL(url);
      return;
    }
    // Native: share the template text so users can save it to Files, Notes,
    // email it to themselves, etc. — there's no in-app download path.
    try {
      await Share.share({
        message: templateCSV,
        title: "Dash-Hound CSV Template",
      });
    } catch {
      // user cancelled — no-op
    }
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#f9fafb" }}>
      {/* Header */}
      <View style={{ flexDirection: "row", alignItems: "center", paddingHorizontal: 16, paddingVertical: 14, backgroundColor: "#fff", borderBottomWidth: 1, borderBottomColor: "#e5e7eb" }}>
        <TouchableOpacity onPress={() => router.back()} style={{ marginRight: 12 }}>
          <Ionicons name="arrow-back" size={22} color="#2563eb" />
        </TouchableOpacity>
        <Text style={{ fontSize: 18, fontWeight: "700", color: "#111827" }}>Import Runs</Text>
      </View>

      <ScrollView contentContainerStyle={{ padding: 20 }}>
        {/* Instructions */}
        <View style={{ backgroundColor: "#eff6ff", borderRadius: 14, padding: 18, marginBottom: 20 }}>
          <Text style={{ fontSize: 15, fontWeight: "700", color: "#1e40af", marginBottom: 8 }}>
            Import runs from a CSV file
          </Text>
          <Text style={{ fontSize: 13, color: "#1e40af", lineHeight: 20 }}>
            Upload a CSV with your run history. Each row needs:{"\n"}
            • <Text style={{ fontWeight: "600" }}>date</Text> (YYYY-MM-DD){"\n"}
            • <Text style={{ fontWeight: "600" }}>time_seconds</Text> (e.g. 7.79){"\n"}
            • <Text style={{ fontWeight: "600" }}>run_number</Text> (1 or 2, optional){"\n"}
            • <Text style={{ fontWeight: "600" }}>notes</Text> (optional)
          </Text>
        </View>

        {/* Template download */}
        <TouchableOpacity
          onPress={downloadTemplate}
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#fff",
            borderRadius: 12,
            padding: 14,
            borderWidth: 1,
            borderColor: "#d1d5db",
            marginBottom: 16,
            gap: 8,
          }}
        >
          <Ionicons name="download-outline" size={18} color="#2563eb" />
          <Text style={{ color: "#2563eb", fontWeight: "600" }}>Download CSV Template</Text>
        </TouchableOpacity>

        {/* File picker — works on web (HTML input), iOS, and Android (DocumentPicker). */}
        <TouchableOpacity
          onPress={handleFilePick}
          style={{
            backgroundColor: "#fff",
            borderRadius: 12,
            padding: 30,
            borderWidth: 2,
            borderColor: "#d1d5db",
            borderStyle: "dashed",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <Ionicons name="cloud-upload-outline" size={36} color="#9ca3af" />
          <Text style={{ color: "#374151", fontWeight: "600", marginTop: 10 }}>Choose CSV File</Text>
          <Text style={{ color: "#9ca3af", fontSize: 12, marginTop: 4 }}>or paste data below</Text>
        </TouchableOpacity>

        {/* Paste area — web uses textarea, native uses TextInput.
            Native: long-press the field and tap Paste to drop your CSV in. */}
        <View style={{ marginBottom: 16 }}>
          <Text style={{ fontSize: 14, fontWeight: "500", color: "#374151", marginBottom: 6 }}>
            {Platform.OS === "web" ? "Or paste CSV data:" : "Paste CSV data (long-press → Paste):"}
          </Text>
          {Platform.OS === "web" ? (
            <textarea
              value={csvText}
              onChange={(e: any) => handlePaste(e.target.value)}
              placeholder="date,time_seconds,run_number,notes&#10;2026-01-15,8.23,1,First event&#10;2026-01-15,7.95,2,Second run"
              rows={6}
              style={{
                width: "100%",
                padding: 12,
                borderRadius: 10,
                border: "1px solid #d1d5db",
                fontFamily: "monospace",
                fontSize: 13,
                resize: "vertical" as any,
              }}
            />
          ) : (
            <TextInput
              value={csvText}
              onChangeText={handlePaste}
              placeholder={"date,time_seconds,run_number,notes\n2026-01-15,8.23,1,First event\n2026-01-15,7.95,2,Second run"}
              multiline
              autoCapitalize="none"
              autoCorrect={false}
              style={{
                backgroundColor: "#fff",
                padding: 12,
                borderRadius: 10,
                borderWidth: 1,
                borderColor: "#d1d5db",
                fontFamily: Platform.OS === "ios" ? "Menlo" : "monospace",
                fontSize: 13,
                minHeight: 140,
                textAlignVertical: "top",
                color: "#111827",
              }}
              placeholderTextColor="#9ca3af"
            />
          )}
        </View>

        {error && (
          <View style={{ backgroundColor: "#fef2f2", padding: 12, borderRadius: 8, marginBottom: 12 }}>
            <Text style={{ color: "#dc2626", textAlign: "center" }}>{error}</Text>
          </View>
        )}

        {/* Parse results */}
        {parsed && (
          <View style={{ backgroundColor: "#fff", borderRadius: 14, borderWidth: 1, borderColor: "#e5e7eb", padding: 16, marginBottom: 16 }}>
            <Text style={{ fontSize: 15, fontWeight: "700", color: "#111827", marginBottom: 10 }}>
              Preview: {parsed.runs.length} runs found
            </Text>

            {parsed.errors.length > 0 && (
              <View style={{ backgroundColor: "#fef2f2", borderRadius: 8, padding: 10, marginBottom: 12 }}>
                <Text style={{ fontSize: 12, fontWeight: "600", color: "#dc2626", marginBottom: 4 }}>
                  {parsed.errors.length} error{parsed.errors.length > 1 ? "s" : ""}:
                </Text>
                {parsed.errors.map((err, i) => (
                  <Text key={i} style={{ fontSize: 12, color: "#dc2626" }}>• {err}</Text>
                ))}
              </View>
            )}

            {/* Preview table */}
            {parsed.runs.length > 0 && (
              <View>
                <View style={{ flexDirection: "row", backgroundColor: "#f3f4f6", borderRadius: 6, padding: 8, marginBottom: 4 }}>
                  <Text style={{ flex: 2, fontSize: 11, fontWeight: "700", color: "#6b7280" }}>Date</Text>
                  <Text style={{ flex: 1, fontSize: 11, fontWeight: "700", color: "#6b7280", textAlign: "right" }}>Time</Text>
                  <Text style={{ flex: 1, fontSize: 11, fontWeight: "700", color: "#6b7280", textAlign: "right" }}>Run #</Text>
                </View>
                {parsed.runs.slice(0, 20).map((run, i) => (
                  <View key={i} style={{ flexDirection: "row", padding: 8, borderBottomWidth: 1, borderBottomColor: "#f3f4f6" }}>
                    <Text style={{ flex: 2, fontSize: 13, color: "#111827" }}>{run.date}</Text>
                    <Text style={{ flex: 1, fontSize: 13, color: "#2563eb", fontWeight: "600", textAlign: "right" }}>{formatTime(run.timeSeconds)}s</Text>
                    <Text style={{ flex: 1, fontSize: 13, color: "#6b7280", textAlign: "right" }}>{run.runNumber}</Text>
                  </View>
                ))}
                {parsed.runs.length > 20 && (
                  <Text style={{ fontSize: 12, color: "#6b7280", textAlign: "center", marginTop: 8 }}>
                    ... and {parsed.runs.length - 20} more
                  </Text>
                )}
              </View>
            )}
          </View>
        )}

        {/* Import result */}
        {importResult && (
          <View style={{ backgroundColor: importResult.failed > 0 ? "#fefce8" : "#f0fdf4", borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <Text style={{ fontSize: 16, fontWeight: "700", color: importResult.failed > 0 ? "#854d0e" : "#15803d" }}>
              Import Complete
            </Text>
            <Text style={{ fontSize: 14, color: importResult.failed > 0 ? "#a16207" : "#16a34a", marginTop: 4 }}>
              {importResult.success} runs imported{importResult.failed > 0 ? `, ${importResult.failed} failed` : ""}
            </Text>
          </View>
        )}

        {/* Import button */}
        {parsed && parsed.runs.length > 0 && !importResult && (
          <View style={{ marginTop: 4 }}>
            {importing ? (
              <View style={{ alignItems: "center", padding: 16 }}>
                <ActivityIndicator size="large" color="#2563eb" />
                <Text style={{ color: "#6b7280", marginTop: 8 }}>Importing runs...</Text>
              </View>
            ) : (
              <Button
                title={`Import ${parsed.runs.length} Runs`}
                onPress={handleImport}
              />
            )}
          </View>
        )}

        {/* Back to profile after import */}
        {importResult && (
          <Button
            title="Back to Dog Profile"
            onPress={() => router.back()}
          />
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
